import styled from "styled-components";

export const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  height: ${({ theme }) => theme.gemscape.staticHeight};

  .seekbar {
    transition: width 0.25s ease-out;
    -moz-transition: none;
  }

  .waveform {
    /* Has to be the same width with .waveform-container */
    cursor: pointer;
    width: 100%;
    height: 64px;
    position: absolute;
    grid-column: 3/4;
  }

  .waveform-static {
    -webkit-filter: ${props =>
      props.hovered
        ? props.isCurrentTrack
          ? "grayscale(0) opacity(0.55)"
          : "grayscale(0) opacity(1)"
        : "grayscale(1) opacity(0.3)"};

    filter: ${props =>
      props.hovered
        ? props.isCurrentTrack
          ? "grayscale(0.25) opacity(0.55)"
          : "grayscale(0) opacity(1)"
        : "grayscale(1) opacity(0.3)"};

    transition: filter 0.2s ease-out,
      transform 0.525s cubic-bezier(0.55, -0.22, 0.63, 1.6),
      opacity 0.05s ease-out;
    transform: ${props =>
      props.suppressAnimation ? "scaleY(1)" : "scaleY(0.4)"};

    &.image-loaded {
      transform: scaleY(1);
    }
  }

  /* When the gemscape is in active tier, remove CSS filters from the SVG
     so overflow: visible isn't defeated by rasterization, and force
     overflow: visible to beat Antd's svg:not(:root){overflow:hidden}. */
  .gemscape-active {
    filter: none;
    -webkit-filter: none;
    overflow: visible;
  }

  /* ── Main player entrance: transform-box needed for base scaleY ── */
  .gemscape-main .base-unplayed,
  .gemscape-main .base-played {
    transform-box: fill-box;
    transform-origin: center;
    /* Ease the saturation/brightness swap (rest ↔ hover ↔ playing) so the
       base silhouette fades like the top shapes instead of snapping. */
    transition: filter 0.4s ease-out;
  }

  /* Hide static image when active inline SVG is present — single paint swap */
  &:has(.gemscape-active) .waveform-static {
    opacity: 0;
    pointer-events: none;
  }

  .waveform-container {
    /* Has to be the same width with .waveform */
    width: 100%;
  }

  .waveform:nth-child(2) {
    z-index: ${props => (props.isMainPlayer === "main-player" ? 160 : 9)};
  }

  /* ── Gemscape active-tier shape styles ────────────────────────── */
  .gemscape-shape {
    transform-box: fill-box;
    transform-origin: center;
    /* Default (recede) speed — applies when classes are removed */
    transition: transform 0.25s ease-out, filter 0.25s ease-out;
  }

  /* Encompassing shapes — gentle scale-up, no glow (glow is hero-only).
     Zero-value drop-shadows keep the filter function list matching hero's
     so the browser can interpolate smoothly instead of discrete-snapping. */
  .gemscape-shape.layer-top.shape-active {
    transform: ${({ theme }) => theme.gemscape.scale.encompassing};
    filter: brightness(1.04) drop-shadow(0 0 0px transparent)
      drop-shadow(0 0 0px transparent);
  }

  /* Hero shape — closest to cursor, stronger scale + frosted glow.
     Fast snap-in (0.06s), slower recede is handled by the base
     .gemscape-shape transition (0.3s ease-out on removal). */
  .gemscape-shape.layer-top.shape-hero {
    transform: ${({ theme }) => theme.gemscape.scale.hero}
      rotate(var(--jit-rot, 0deg)) skewX(var(--jit-skew, 0deg))
      scale(var(--jit-sx, 1), var(--jit-sy, 1));
    transition: transform 0.06s ease-out, filter 0.06s ease-out;
    filter: brightness(1.06)
      drop-shadow(0 0 8px var(--shape-color, rgba(255, 255, 255, 0.1)))
      drop-shadow(0 0 5px var(--shape-color, rgba(255, 255, 255, 0.06)));
  }

  /* Slightly slower recede when hero is demoted back to encompassing
     or inactive — override the base transition */
  .gemscape-shape.shape-active:not(.shape-hero) {
    transition: transform 0.3s ease-out, filter 0.6s ease-out;
  }

  /* ── Playing track: top-layer grayscale until progress passes center ──
     Split brightness + opacity: brightness(0.6) darkens each shape
     in-place (no alpha stacking) while opacity(0.55) lets brighter
     shapes beneath bleed through less than pure brightness could.
     Milder than the original opacity(0.3) so 6+ stacked shapes
     don't compound into an opaque blob. */
  .reveal-playing .gemscape-shape.layer-top {
    filter: grayscale(1) brightness(0.6) opacity(0.55)
      drop-shadow(0 0 0px transparent) drop-shadow(0 0 0px transparent);
    transition: transform 2.5s ease-out, filter 0.4s ease-out;
  }

  /* Once progress passes the shape's center, fade to full color.
     Filter functions MUST match the base rule (grayscale + brightness)
     so the browser can interpolate smoothly instead of snapping. */
  .reveal-playing .gemscape-shape.layer-top.shape-played {
    filter: grayscale(0) brightness(1) opacity(1)
      drop-shadow(0 0 0px transparent) drop-shadow(0 0 0px transparent);
    transition: transform 2.5s ease-out, filter 1s ease-out;
  }

  /* ── Hover reveal: same B&W → color sweep, driven by cursor x ──────
     Top shapes rest grayscale; those left of the cursor colorize.
     Mirrors the playing-mode rules but with snappier timing since
     it tracks the pointer rather than slow playback progress. */
  .reveal-hover .gemscape-shape.layer-top {
    filter: grayscale(1) brightness(0.6) opacity(0.55)
      drop-shadow(0 0 0px transparent) drop-shadow(0 0 0px transparent);
    transition: transform 0.3s ease-out, filter 0.2s ease-out;
  }
  .reveal-hover .gemscape-shape.layer-top.shape-played {
    filter: grayscale(0) brightness(1) opacity(1)
      drop-shadow(0 0 0px transparent) drop-shadow(0 0 0px transparent);
    transition: transform 0.3s ease-out, filter 0.25s ease-out;
  }

  /* Hover feedback during playback — native :hover on the shape directly
     under the cursor.  :not guards ensure the pulse-driven classes
     always take precedence. */
  .reveal-playing
    .gemscape-shape.layer-top:hover:not(.shape-active):not(.shape-hero) {
    transform: ${({ theme }) => theme.gemscape.scale.hover};
    filter: grayscale(1) brightness(0.94) opacity(0.65)
      drop-shadow(0 0 0px transparent) drop-shadow(0 0 0px transparent);
    transition: transform 0.12s ease-out, filter 0.12s ease-out;
  }
  .reveal-playing
    .gemscape-shape.layer-top:hover:active:not(.shape-active):not(.shape-hero) {
    transform: ${({ theme }) => theme.gemscape.scale.hover};
    filter: grayscale(0.5) brightness(1.05) opacity(0.8)
      drop-shadow(0 0 2px var(--shape-color, rgba(255, 255, 255, 0.2)))
      drop-shadow(0 0 1px var(--shape-color, rgba(255, 255, 255, 0.1)));
    transition: transform 0.04s ease-out, filter 0.04s ease-out;
  }
  .reveal-playing
    .gemscape-shape.layer-top.shape-played:hover:not(.shape-active):not(.shape-hero) {
    transform: ${({ theme }) => theme.gemscape.scale.hover};
    filter: grayscale(0) brightness(1.35) opacity(1)
      drop-shadow(0 0 0px transparent) drop-shadow(0 0 0px transparent);
    transition: transform 0.12s ease-out, filter 0.12s ease-out;
  }
  .reveal-playing
    .gemscape-shape.layer-top.shape-played:hover:active:not(.shape-active):not(.shape-hero) {
    transform: ${({ theme }) => theme.gemscape.scale.hover};
    filter: grayscale(0) brightness(1.5) opacity(1)
      drop-shadow(0 0 3px var(--shape-color, rgba(255, 255, 255, 0.3)))
      drop-shadow(0 0 1px var(--shape-color, rgba(255, 255, 255, 0.15)));
    transition: transform 0.04s ease-out, filter 0.04s ease-out;
  }

  /* Hover feedback in browse mode — hero-style glow on the shape nearest
     the cursor. Driven by the JS-stamped .shape-hero class (resting-bounds
     fraction), NOT native :hover — so the growing transform can't move its
     own edge off the cursor and cause hover on/off stutter. */
  .reveal-hover .gemscape-shape.layer-top.shape-hero {
    transform: ${({ theme }) => theme.gemscape.scale.hero}
      rotate(var(--jit-rot, 0deg)) skewX(var(--jit-skew, 0deg))
      scale(var(--jit-sx, 1), var(--jit-sy, 1));
    transition: transform 0.35s ease-out, filter 0.06s ease-out;
    filter: brightness(1.38)
      drop-shadow(0 0 8px var(--shape-color, rgba(255, 255, 255, 0.1)))
      drop-shadow(0 0 5px var(--shape-color, rgba(255, 255, 255, 0.06)));
  }
  .reveal-hover .gemscape-shape.layer-top.shape-hero:active {
    transform: ${({ theme }) => theme.gemscape.scale.hero}
      rotate(var(--jit-rot, 0deg)) skewX(var(--jit-skew, 0deg))
      scale(var(--jit-sx, 1), var(--jit-sy, 1));
    transition: transform 0.35s ease-out, filter 0.04s ease-out;
    filter: brightness(1.55)
      drop-shadow(0 0 12px var(--shape-color, rgba(255, 255, 255, 0.14)))
      drop-shadow(0 0 7px var(--shape-color, rgba(255, 255, 255, 0.08)));
  }

  /* Active/hero shapes during playback — driven by either mouse hover
     or playback progress. Pulse-responsive so they breathe with audio.
     Include .layer-top so specificity (4 classes) beats the
     .shape-played rule and the hero glow isn't overridden. */
  .reveal-playing .gemscape-shape.layer-top.shape-active {
    transform: ${({ theme }) => theme.gemscape.scale.activePlay};
    filter: grayscale(0) brightness(1.04) opacity(1)
      drop-shadow(0 0 0px transparent) drop-shadow(0 0 0px transparent);
    transition: transform 2s ease-out, filter 0.15s ease-out;
  }
  .reveal-playing .gemscape-shape.layer-top.shape-hero {
    transform: ${({ theme }) => theme.gemscape.scale.heroPlay}
      rotate(var(--jit-rot, 0deg)) skewX(var(--jit-skew, 0deg))
      scale(var(--jit-sx, 1), var(--jit-sy, 1));
    transition: transform 0.7s cubic-bezier(0.05, 0.85, 0.15, 1),
      filter 1s ease-out;
    filter: grayscale(0) brightness(1.06) opacity(0.92)
      drop-shadow(0 0 8px var(--shape-color, rgba(255, 255, 255, 0.1)))
      drop-shadow(0 0 5px var(--shape-color, rgba(255, 255, 255, 0.06)));
  }

  /* Pulse onset: fast attack to peak brightness (class toggled by JS) */
  &.pulse-on .reveal-playing .gemscape-shape.layer-top.shape-hero {
    filter: grayscale(0) brightness(1.25) opacity(0.95)
      drop-shadow(0 0 10px var(--shape-color, rgba(255, 255, 255, 0.14)))
      drop-shadow(0 0 6px var(--shape-color, rgba(255, 255, 255, 0.08)));
    transition: transform 0.05s ease-out, filter 0.05s ease-out;
  }

  /* Playing track: full grayscale for unplayed portion. */
  .reveal-playing .base-unplayed {
    filter: grayscale(1) brightness(0.6) opacity(0.55);
  }

  /* Hovered non-playing track: full grayscale for unplayed portion,
     matching the playing-mode reveal. */
  .reveal-hover .base-unplayed {
    filter: grayscale(1) brightness(0.6) opacity(0.55);
  }

  /* SVG clip-path transition for smooth progress reveal */
  .base-progress-rect {
    transition: width 0.25s ease-out;
  }

  /* Progress overlay for non-active tier (paused + not hovered) */
  .progress-overlay {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    background: ${({ theme }) => theme.gemscape.progressOverlay};
    pointer-events: none;
    z-index: 5;
    transition: width 0.25s ease-out;
  }
`;
